import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import { useAuth } from './AuthContext';
import { useI18n } from '../i18n/I18nContext';
import getApiBase from '../utils/apiBase';
import './AdminAnalytics.css';

const API = `${getApiBase()}/api/admin/analytics`;
const RANGES = [1, 7, 30, 90];
const AUDIENCES = ['all', 'guest', 'user'];
const METRICS = ['pageViews', 'clicks', 'visitors', 'sessions', 'guestVisitors', 'userVisitors'];
const STAGES = ['page_view', 'click', 'add_to_cart', 'remove_from_cart', 'checkout_start', 'checkout_redirect', 'login', 'register', 'custom_request_submit', 'contact_submit'];
const ACTION_LABELS = {
  add_to_cart: 'add_to_cart',
  cart_checkout: 'checkout_start',
  checkout_start: 'checkout_start',
  remove_from_cart: 'remove_from_cart',
  login: 'login',
  register: 'register',
  custom_request_submit: 'custom_request_submit',
  contact_submit: 'contact_submit',
};
const TARGET_LABELS = {
  nav_home: 'nav.home',
  nav_store: 'nav.store',
  nav_customizer: 'nav.customYoyo',
  nav_custom_design: 'nav.customDesign',
  nav_team: 'nav.team',
  nav_stories: 'nav.stories',
  nav_video_scoring: 'nav.videoScoring',
  nav_community: 'nav.community',
  nav_guide: 'nav.guide',
  nav_shipping: 'nav.shipping',
  nav_contact: 'nav.contact',
  header_cart: 'header.cart',
  hero_store: 'home.shop',
  home_store: 'home.shop',
  home_team: 'nav.team',
  carousel_product: 'analytics.product',
  'product.add_to_cart': 'product.addToCart',
  'customizer.add_to_cart': 'product.addToCart',
  'cart.checkout': 'cart.checkout',
  'cart.remove': 'cart.remove',
  'custom_design.start': 'design.hero.start',
  'custom_design.upload': 'design.upload.choose',
  'custom_design.submit': 'design.submit.review',
  'contact.submit': 'contact.submit',
  'store.filters_clear': 'store.clearFilters',
  'store.pagination.previous': 'store.previousPage',
  'store.pagination.next': 'store.nextPage',
  'store.pagination.page': 'store.pagination',
  'store.filter.brand': 'store.filter.brand',
  'store.filter.type': 'store.filter.type',
  'store.filter.group': 'store.filter.group',
};

function readAdminToken() {
  try { return localStorage.getItem('adminAuth') || ''; } catch { return ''; }
}

function saveAdminToken(token) {
  try {
    if (token) localStorage.setItem('adminAuth', token);
    else localStorage.removeItem('adminAuth');
  } catch {
    // Administrator access still works for this page when storage is unavailable.
  }
}

export default function AdminAnalytics() {
  const { user, loading: authLoading } = useAuth();
  const { t, formatNumber, formatDate } = useI18n();
  const [auth, setAuth] = useState(readAdminToken);
  const [login, setLogin] = useState({ username: '', password: '' });
  const [sessionDenied, setSessionDenied] = useState(false);
  const [days, setDays] = useState(7);
  const [audience, setAudience] = useState('all');
  const [refresh, setRefresh] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const authorized = Boolean(auth || (user?.role === 'admin' && !sessionDenied));

  useEffect(() => {
    if (authLoading || !authorized) return undefined;
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError('');
    setData(null);
    fetch(`${API}?days=${days}&audience=${audience}`, {
      credentials: 'include',
      headers: auth ? { Authorization: `Basic ${auth}` } : {},
      signal: controller.signal,
    }).then(async response => {
      if (!active) return;
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          setLoading(false);
          saveAdminToken('');
          setAuth('');
          setSessionDenied(true);
          setError('authFailed');
        } else {
          setError(response.status === 429 ? 'rateLimited' : 'error');
        }
        return;
      }
      const body = await response.json();
      if (active) {
        setData(body);
        if (auth) saveAdminToken(auth);
      }
    }).catch(() => {
      if (active) setError('error');
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
      controller.abort();
    };
  }, [auth, authLoading, authorized, days, audience, refresh]);

  const handleLogin = event => {
    event.preventDefault();
    if (login.username.includes(':')) {
      setError('authFailed');
      return;
    }
    try {
      const bytes = new TextEncoder().encode(`${login.username}:${login.password}`);
      const token = btoa(Array.from(bytes, byte => String.fromCharCode(byte)).join(''));
      setAuth(token);
      setSessionDenied(false);
      setLogin(current => ({ ...current, password: '' }));
      setRefresh(current => current + 1);
    } catch {
      setError('authFailed');
    }
  };

  const clearCredentials = () => {
    saveAdminToken('');
    setAuth('');
    setData(null);
    setError('');
    setLogin({ username: '', password: '' });
  };
  const number = value => formatNumber(value ?? 0);
  const dateTime = value => value ? formatDate(value, { dateStyle: 'short', timeStyle: 'short' }) : t('analytics.unavailable');
  const eventLabel = name => t(`analytics.stage.${STAGES.includes(name) ? name : 'other'}`);
  const targetLabel = target => {
    if (Object.prototype.hasOwnProperty.call(TARGET_LABELS, target)) return t(TARGET_LABELS[target]);
    if (Object.prototype.hasOwnProperty.call(ACTION_LABELS, target)) return eventLabel(ACTION_LABELS[target]);
    return <code>{target}</code>;
  };
  const rows = key => Array.isArray(data?.[key]) ? data[key] : [];
  const empty = <p className="analytics-empty">{t('analytics.noData')}</p>;

  return <main className="admin-analytics">
    <Seo title={t('analytics.title')} description={t('analytics.description')} path="/admin/analytics" noindex />
    <header className="analytics-header">
      <div><h1>{t('analytics.title')}</h1><p>{t('analytics.intro')}</p><Link to="/admin">{t('analytics.back')}</Link></div>
      {auth && <button type="button" onClick={clearCredentials}>{t('analytics.clearCredentials')}</button>}
    </header>
    {error && <div className="analytics-error" role="alert"><p>{t(`analytics.${error}`)}</p>{authorized && <button type="button" disabled={loading} onClick={() => setRefresh(current => current + 1)}>{t('analytics.retry')}</button>}</div>}
    {authLoading ? <p role="status">{t('analytics.loading')}</p> : !authorized ? <section className="analytics-login">
      <h2>{t('analytics.login')}</h2><p>{t('analytics.denied')}</p>
      <form onSubmit={handleLogin}>
        <label>{t('analytics.username')}<input autoComplete="username" maxLength={100} required value={login.username} onChange={event => setLogin(current => ({ ...current, username: event.target.value }))} /></label>
        <label>{t('analytics.password')}<input type="password" autoComplete="current-password" maxLength={256} required value={login.password} onChange={event => setLogin(current => ({ ...current, password: event.target.value }))} /></label>
        <button type="submit">{t('analytics.signIn')}</button>
      </form>
    </section> : <>
      <section className="analytics-filters">
        <label>{t('analytics.range')}<select value={days} onChange={event => setDays(Number(event.target.value))}>{RANGES.map(range => <option value={range} key={range}>{range === 1 ? t('analytics.oneDay') : t('analytics.days', { count: number(range) })}</option>)}</select></label>
        <label>{t('analytics.audience')}<select value={audience} onChange={event => setAudience(event.target.value)}>{AUDIENCES.map(value => <option value={value} key={value}>{t(`analytics.${value}`)}</option>)}</select></label>
        <button type="button" disabled={loading} onClick={() => setRefresh(current => current + 1)}>{t('analytics.refresh')}</button>
      </section>
      {loading && <p role="status">{t('analytics.loading')}</p>}
      {data && <div aria-busy={loading}>
        <div className="analytics-metrics">{METRICS.map(metric => <article key={metric} aria-label={t(`analytics.${metric}`)}><span>{t(`analytics.${metric}`)}</span><strong>{number(data.summary?.[metric])}</strong></article>)}</div>
        <p className="analytics-note">{t('analytics.countNote')}</p>
        <section><h2>{t('analytics.daily')}</h2><p className="analytics-note">{t('analytics.utcNote')}</p>{rows('daily').length ? <>
          <DailyTrend points={rows('daily')} t={t} />
          <AnalyticsTable columns={['date', 'pageViews', 'clicks', 'visitors']} t={t}>{rows('daily').map(row => <tr key={row.date}><td>{formatDate(row.date, { dateStyle: 'medium', timeZone: 'UTC' })}</td><td>{number(row.pageViews)}</td><td>{number(row.clicks)}</td><td>{number(row.visitors)}</td></tr>)}</AnalyticsTable>
        </> : empty}</section>
        <div className="analytics-columns">
          <section><h2>{t('analytics.pages')}</h2>{rows('pages').length ? <AnalyticsTable columns={['page', 'pageViews', 'visitors']} t={t}>{rows('pages').map(row => <tr key={row.page}><td><code>{row.page}</code></td><td>{number(row.pageViews)}</td><td>{number(row.visitors)}</td></tr>)}</AnalyticsTable> : empty}</section>
          <section><h2>{t('analytics.targets')}</h2>{rows('targets').length ? <AnalyticsTable columns={['target', 'page', 'destination', 'clicks']} t={t}>{rows('targets').map((row, index) => <tr key={`${row.page}:${row.target}:${row.targetPath}:${index}`}><td>{targetLabel(row.target)}</td><td><code>{row.page}</code></td><td>{row.targetPath ? <code>{row.targetPath}</code> : t('analytics.unavailable')}</td><td>{number(row.clicks)}</td></tr>)}</AnalyticsTable> : empty}</section>
        </div>
        <div className="analytics-columns">
          <section><h2>{t('analytics.transitions')}</h2>{rows('transitions').length ? <AnalyticsTable columns={['from', 'to', 'count']} t={t}>{rows('transitions').map(row => <tr key={`${row.from}:${row.to}`}><td><code>{row.from}</code></td><td><code>{row.to}</code></td><td>{number(row.count)}</td></tr>)}</AnalyticsTable> : empty}</section>
          <section><h2>{t('analytics.funnel')}</h2><p className="analytics-note">{t('analytics.funnelNote')}</p>{rows('funnel').length ? <AnalyticsTable columns={['action', 'sessions', 'events']} t={t}>{rows('funnel').map(row => <tr key={row.name}><td>{eventLabel(row.name)}</td><td>{number(row.sessions)}</td><td>{number(row.events)}</td></tr>)}</AnalyticsTable> : empty}</section>
        </div>
        <section><h2>{t('analytics.recentSessions')}</h2>{rows('recentSessions').length ? <AnalyticsTable columns={['sessionId', 'audience', 'startedAt', 'lastSeen', 'events', 'path']} t={t}>{rows('recentSessions').map(row => <tr key={`${row.sessionId}:${row.audience}`}><td><code>{row.sessionId}</code></td><td>{t(`analytics.${row.audience === 'user' ? 'user' : 'guest'}`)}</td><td>{dateTime(row.startedAt)}</td><td>{dateTime(row.lastSeen)}</td><td>{number(row.eventCount)}</td><td className="analytics-session-path">{Array.isArray(row.path) && row.path.length ? row.path.map((path, index) => <React.Fragment key={`${path}:${index}`}>{index > 0 && <span aria-hidden="true"> → </span>}<code>{path}</code></React.Fragment>) : t('analytics.unavailable')}</td></tr>)}</AnalyticsTable> : empty}</section>
      </div>}
    </>}
  </main>;
}

function AnalyticsTable({ columns, children, t }) {
  return <div className="analytics-table"><table><thead><tr>{columns.map(column => <th scope="col" key={column}>{t(`analytics.${column}`)}</th>)}</tr></thead><tbody>{children}</tbody></table></div>;
}

function DailyTrend({ points, t }) {
  const max = Math.max(1, ...points.flatMap(point => [point.pageViews || 0, point.clicks || 0]));
  const coordinates = key => points.map((point, index) => `${points.length > 1 ? index / (points.length - 1) * 600 : 300},${110 - (point[key] || 0) / max * 100}`).join(' ');
  return <div className="analytics-trend">
    <div className="analytics-legend"><span className="analytics-view-key">{t('analytics.pageViews')}</span><span className="analytics-click-key">{t('analytics.clicks')}</span></div>
    <svg viewBox="-6 0 612 120" role="img" aria-label={t('analytics.chartDescription')}>
      <polyline points={coordinates('pageViews')} className="analytics-view-line" /><polyline points={coordinates('clicks')} className="analytics-click-line" />
      {points.length === 1 && ['pageViews', 'clicks'].map(key => <circle key={key} cx="300" cy={110 - (points[0][key] || 0) / max * 100} r="4" className={key === 'pageViews' ? 'analytics-view-dot' : 'analytics-click-dot'} />)}
    </svg>
  </div>;
}
